import React from "react";
import styles from "../../css/modules/projectDetails.module.scss";
import { BryntumGantt } from "@bryntum/gantt-react";
import { generateDataset, getGanttConfig } from "./utils/GanttConfig";
import { SyncProject } from "./components/SyncProject";
import { apiUrls } from "../../utils/api";
import { changePageTitle } from "../../redux/pageTitle";
import { connect } from "react-redux";
import { Loading } from "../../components/Loading";
import { projectDetails } from "../../redux/projectDetails";
import { saveChanges, updateTaskNote } from "./utils/saveChanges";
import { Box, Button, Snackbar } from "@mui/material";
import { Alert } from "@mui/material";
import { defaultViewPreset, routes } from "../../utils/constants";
import { StopPageRefresh } from "../../components/StopPageRefresh";
import {
  beforeAddTask,
  beforeRemoveTask,
  beforeUpdateTask,
  departmentSortResources,
  getViewPresets,
  ridderTypes,
  updateTask,
  checkResources,
  getTaskById,
  sameDay,
  applyColumnFilters
} from "../../utils/ganttUtils";
import { AsyncHelper, DateHelper, Mask, ProjectModel, ResourceHistogram, Toast } from "@bryntum/gantt/gantt.module";
import { translate } from "../../utils/translate";
import { observer } from "../../utils/observer";
import { Allotment } from "allotment";
import { ToolBar } from "./components/ToolBar";
import { withPageApi } from "./components/PageApi";
import { histogramTooltipFormat } from "../../utils/format";
import { UsersOnProject } from "../../components/Toolbar/UsersOnPage";
import { withAccess } from "../../utils/withAccess";
import { AccessEntries, PAGE_ACCESS } from "../../utils/accessEntries";
import { beforeRefreshPopup } from "./utils/utils";
import { snackbar } from "../../redux/snackbar";
import { ApiClientContext } from "../../components/ApiProvider";
import { ViewTypes } from "../../utils/constants";

export class Project extends React.Component {
  static contextType = ApiClientContext;
  constructor(props) {
    super(props);
    this.histogramRef = React.createRef();
    this.configs = this.props.setting.screenLayoutSingleViewConfig;

    this.state = {
      loading: false,
      loadingText: translate("Loading..."),
      newExtraText: null,
      loadingExtraText: [],
      levels: null,
      currentProject: null,
      baseLineOptions: null,
      selectedBaselineProject: null,
      projectOutdated: false,
      projectOutdatedMessage: null,
      hasChanges: false,
      exportInProgress: false,
      isHistogramVisible: this.configs.showHistogram,
      dataReady: false,
      columnFilters: null,
    };
    this.queryParams = new URLSearchParams(document.location.search);
    this.baselineProject = this.queryParams.get("baseline") || null;
    this.generalProjectSettings = this.props.setting.generalProjectSettings;
    this.ganttConfig = getGanttConfig(this.configs, this.generalProjectSettings, this);
    this.autoSaveEnabled = this.configs.autoSave || false;
    this.baselineProjects = [];
  }

  componentDidMount() {
    this.getTasks(this.props.match.params.id, this.baselineProject);
    this.unsubscribeSaveErrror = observer.subscribe("notSavedTasks", this.onSaveError);
  }

  componentWillUnmount() {
    clearTimeout(this.saveTimer);
    this.leavePageAbort?.abort();
    this.unsubscribeSaveErrror && this.unsubscribeSaveErrror();
    this.props.dispatch(snackbar.actions.remove("save-error-alert"));
  }

  componentDidUpdate(prevProps) {
    if (prevProps.setting.screenLayoutSingleViewConfig !== this.props.setting.screenLayoutSingleViewConfig) {
      this.configs = this.props.setting.screenLayoutSingleViewConfig;
    }
  }

  onSaveError = (notSavedTaskIds = []) => {
    const tasksWithIssues = notSavedTaskIds.map((id) => this.g.project.taskStore.getById(id)).filter((i) => !!i);
    let msg = "";
    if (tasksWithIssues.length === 1) {
      msg = translate("saveTaskOneError", {
        task: tasksWithIssues[0].description,
      });
    }
    if (tasksWithIssues.length > 1) {
      const task = tasksWithIssues[0];
      msg = translate("saveTaskManyError", {
        task: task.description,
        restAmount: tasksWithIssues.length - 1,
      });
    }

    if (msg) {
      this.showSnackBarMessage(msg, this.onRefreshProject);
    }
  };

  showSnackBarMessage(msg, refreshButton) {
    this.props.dispatch(
      snackbar.actions.add({
        id: "save-error-alert",
        severity: "warning",
        message: (
          <Box display="flex" alignItems="center">
            {msg}&nbsp;&nbsp;
            {refreshButton && (
              <Button size="small" variant="contained" color="secondary" onClick={refreshButton}>
                {translate("Refresh")}
              </Button>
            )}
          </Box>
        ),
      })
    );
  }

  showHistogram = async () => {
    this.setState({
      isHistogramVisible: true,
    });
    await AsyncHelper.sleep(500);
  };

  hideHistogram = () => {
    return this.setState({
      isHistogramVisible: false,
    });
  };

  addCustomToolbarItems = async (currentProject) => {
    this.props.update({
      baselineEnabled: !currentProject.Blocked && currentProject.TypeString === "Actual",
      simulationEnabled: currentProject?.TypeString === "Simulation",
    });
  };

  onProjectChanged = (projectId, username, source) => {
    if (projectId == this.projectID) {
      const viewName = translate(source);
      const projectOutdatedMessage = translate("ProjectOutdatedPleaseRefreshWithUserInfo", { username, viewName });

      this.setState({
        projectOutdated: true,
        projectOutdatedMessage: projectOutdatedMessage,
        hasChanges: false,
      });
    }
  };

  saveTimer = null;
  autoSaveDuration = 5000;

  setAutoSave = () => {
    if (!this.saveTimer) {
      this.saveTimer = setTimeout(() => {
        if (this.autoSaveEnabled) {
          this.saveProject();
        } else {
          this.saveTimer = null;
        }
      }, this.autoSaveDuration);
    }
  };

  saveProject = async () => {
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    if (this.state.projectOutdated || this.state.currentProject.Blocked || this.state.currentProject.Archived) return;
    const gantt = this.g;

    const saveMask = Mask.mask(
      {
        text: this.props.t("Save changes"),
        mode: "dark",
      },
      document.querySelector("." + styles.ganttRoot)
    );
    gantt.deselectAll();
    await saveChanges(gantt.project, saveMask, ViewTypes.ProjectView, this.context);
    if (this.projectData.project) {
      this.projectData.project.NewProject = false;
      this.setPageTitle(this.projectData);
    }
    await this.updateHistogram();
    this.setState(
      {
        hasChanges: false,
      },
      () => {
        gantt.project.clearChanges();
        this.props.update({
          canSave: false,
        });
        if (this.generalProjectSettings?.AutoSyncAfterSave) {
          const unsubscribe = observer.subscribe("SyncProjectFinished", () => {
            unsubscribe();
            saveMask.doDestroy();
            this.getTasks(this.props.match.params.id, this.baselineProject);
          });
          saveMask.text = translate("project.syncStarted");
          observer.trigger("SyncProject");
        } else {
          saveMask.doDestroy();
        }
      }
    );
  };

  get g() {
    return this.props.api.gantt;
  }

  get projectID() {
    return Number(this.props.match.params.id);
  }

  setDataReady() {
    if (!this.g.project?.changes) {
      this.setState({
        hasChanges: false,
        dataReady: true,
      });

      this.props.update({
        canSave: false,
      });
    } else {
      this.props.update({
        canSave: true,
      });
      this.setState({
        hasChanges: true,
        dataReady: true,
      });
    }
  }

  processLoadChanges(loadedProjectTask) {
    if (!this.g.project?.changes) {
      return;
    }

    var t0 = performance.now();
    try {
      this.g.project.calendarManagerStore.commit(); //Ignore changes on calendars
      this.g.project.dependencyStore.commit(); //Ignore changes on dependencies
      if (this.g.project?.changes?.tasks?.updated) {
        const dateFields = [
          "constraintDate",
          "earlyEndDate",
          "earlyStartDate",
          "endDate",
          "lateEndDate",
          "lateStartDate",
          "startDate",
        ];
        for (let ganttTaskChanges of this.g.project?.changes.tasks.updated) {
          let taskJson = getTaskById(loadedProjectTask, ganttTaskChanges.id);
          let blnIgnoreChanges = true;
          if (taskJson) {

            for (const prop in ganttTaskChanges) {
              if (prop == "id") {
                continue;
              }

              if (prop == "percentDone") {
                if (taskJson[prop] && ganttTaskChanges[prop] && Math.abs(taskJson[prop] - ganttTaskChanges[prop]) < 0.1) {
                  //Ignore rounding errors
                  continue;
                } else if (
                  (taskJson.percentDone == 99 && ganttTaskChanges.percentDone == 100) ||
                  (taskJson.percentDone == 100 && ganttTaskChanges.percentDone == 99)
                ) {
                  //Ignore 99/100 this is forced based on completeness
                  continue;
                } else {
                  blnIgnoreChanges = false;
                  break;
                }
              }

              if (taskJson.blocked && ((prop == 'startDate') || (prop == 'endDate'))) {
                const calculatedDate = new Date(ganttTaskChanges[prop]); //make sure it is a date object. Sometimes it is a string or number
                const loadedDate = new Date(taskJson[prop]); 

                if (!sameDay(calculatedDate, loadedDate)) {
                  const loadedDayOfWeek = loadedDate.getDay();
                  if ((loadedDayOfWeek == 0 || loadedDayOfWeek == 6)) {
                    const diffDays = Math.floor(Math.abs(calculatedDate - loadedDate) / (1000 * 60 * 60 * 24));
                    console.log(diffDays + " days");
                    if (diffDays <= 2) {
                      continue; //All tasks are pushed to the next monday, ignore this change
                    }
                  }
                  blnIgnoreChanges = false;
                  break;
                }
              }

              if (!taskJson[prop]) {
                blnIgnoreChanges = false;
                break;
              }

              // Check if the only fields present are the date fields and the id fields
              //if the date fields only differ in timezone or time, ignore the changes
              //Split on + to only ignore timezones, on T to ignore the time entirely
              if (ganttTaskChanges[prop] && (dateFields.indexOf(prop) != -1)) {
                const calculatedDate = new Date(ganttTaskChanges[prop]);
                const loadedDate = new Date(taskJson[prop]);

                if (!sameDay(calculatedDate, loadedDate)) {
                  blnIgnoreChanges = false;
                  break;
                }
              }
            }

          }

          if (blnIgnoreChanges) {
            this.g.project.taskStore.getById(ganttTaskChanges.id)?.clearChanges(false);
          }
        }
      }
      var t1 = performance.now();
      console.log(`Call to processLoadChanges took ${t1 - t0} milliseconds`);
    }
    catch (error) {
      console.error("An error occurred in function processLoadChanges: " + error);
    }

  }

  getProjectListeners() {
    let once = true;
    return {
      load: async () => {
        if (once) {
          once = false;

          this.processLoadChanges(this.tasksData[0]);
          this.setDataReady();
          this.g.subGrids.locked.width = this.configs.taskGridWidth ? this.configs.taskGridWidth + "%" : "60%";
          const locationState = this.props.location.state;
          const targetTask = this.queryParams.get("targetTask");
          const task = targetTask && this.g.project.taskStore.getById(targetTask);
          if (task) {
            await this.g.expandTo(task);
            await this.g.selectRow({ record: task, scrollIntoView: true });
            await this.g.scrollTaskIntoView(task, { edgeOffset: 20, block: "start" });
            // duplication is needed because it's not working correct from firs time
            await this.g.scrollTaskIntoView(task, { edgeOffset: 20, block: "start" });
          } else if (locationState?.zoomLevel) {
            try {
              locationState.regions.forEach((region, index) => {
                if (index + 1 !== locationState.regions.length) {
                  this.g.subGrids[region.key].width = region.width; // last grid don't need the width because it has a bug
                }
              });
              this.g.zoomLevel = locationState.zoomLevel;
              if (locationState.startDate) {
                this.g.scrollToDate(locationState.startDate, { block: "start", animate: true });
              }
            } catch (err) {
              console.log(err);
            }
          } else if (this.configs.zoomToFitOnLoad) {
            this.g?.zoomToFit({
              leftMargin: 50,
              rightMargin: 50,
            });
          }

          if (locationState?.columnFilters) {
            applyColumnFilters(locationState.columnFilters, this.g.project.taskStore);
          }
        }
     
        this.g.taskStore.on("beforeUpdate", beforeUpdateTask);
        this.g.assignmentStore.on("beforeUpdate", this.onBeforeAssignmentUpdate);
      },
      hasChanges: () => {
        if (
          (!this.state.currentProject.Blocked || !this.state.currentProject.Archived) &&
          !this.state.projectOutdated &&
          this.state.dataReady
        ) {
          this.props.update({
            canSave: true,
          });
          this.setState({
            hasChanges: true,
          });
          this.setAutoSave();
        }
      },
      noChanges: ({ source }) => {
        const hasChanges = !!source.changes;
        this.props.update({
          canSave: hasChanges,
        });
        this.setState({
          hasChanges: hasChanges,
        });
        clearTimeout(this.saveTimer);
        this.saveTimer = null;
      },
    };
  }
  showEmptyDurationAlert = (tasks = []) => {
    let msg;
    const vars = {
      task: tasks[0].name,
      restAmount: tasks.length - 1,
    };
    if (tasks.length === 1) {
      msg = translate("emptyDurationOneError", vars);
    } else {
      msg = translate("emptyDurationManyError", vars);
    }
    this.showAlert(msg);
  };

  showAlert = (msg) => {
    this.props.dispatch(
      snackbar.actions.add({
        id: "warn-empty-duration",
        severity: "warning",
        message: (
          <Box display="flex" alignItems="center">
            {msg}
          </Box>
        ),
      })
    );
  };

  onBeforeAssignmentUpdate = ({ source, record, changes }) => {
    if (changes.units == 100 && this?.state?.dataReady && record.resource?.calendar && record.event) {
      let hasWorkingTime = record.resource?.calendar?.isWorkingTime(record.event?.startDate, record.event?.endDate);
      if (!hasWorkingTime) {
        this.showAlert(translate("There is no available capacity at this date"));
        return false;
      }
    }
  };

  applyEvents = (project) => {
    project.on({
      dataReady: () => {
        const emptyDurationTasks = [];
        project.taskStore.forEach((task) => {
          if (!task.duration && !task.isMilestone) {
            emptyDurationTasks.push(task);
          }
        });
        if (emptyDurationTasks?.length) {
          this.showEmptyDurationAlert(emptyDurationTasks);
        }
      },
    });
    project.taskStore.on({
      beforeAdd: beforeAddTask,
      beforeRemove: beforeRemoveTask,
      update: updateTask,
      beforeSort: (e) => {
        /*if (project.changes && e.sorters && e.sorters.length > 0) {
          Toast.show(translate("sortAllowedOnlyWithoutChanges"));
          return false;
        }*/
        if (!e.sorters?.length) {
          e.sorters.push({ field: "index", ascending: true });
        }
      },
    });
    project.taskStore.on({
      update: (source) => {
        if (source?.record?.isTask && !source?.record.duration && !source?.record.isMilestone) {
          this.showEmptyDurationAlert([source.record]);
        }
        if (source.changes.hasOwnProperty("endDate") || source.changes.hasOwnProperty("startDate")) {
          const project = this.histogramProject;
          if (project) {
            const task = project.taskStore.getById(source.record.id);
            task?.set({
              startDate: source.record.startDate,
              endDate: source.record.endDate,
            });
          }

          if (source.record && source.record.schedulingMode === "FixedEffort") {
            if (source.changes.hasOwnProperty("duration")) {
              //reset schedulingMode
              const oldStart = new Date(source.record.get("startDate"));
              const oldEnd = new Date(source.record.get("endDate"));
              source.record.set("schedulingMode", "FixedDuration");
              source.record.startDate = oldStart;
              source.record.endDate = oldEnd;
              source.record.commitAsync();
            } else {
              //make sure we keep 100% units in the assignments
              //TODO
            }
          }
        }

        if (source.changes.hasOwnProperty("note")) {
          updateTaskNote(source.record.id, source.changes.note.value, this.context);
        }
      },
    });
    project.assignmentStore.on({
      remove: (source) => {
        if (this.histogram) {
          source.records.forEach((record) => {
            const i = this.histogram.project.assignmentStore.getById(record.id);
            if (i) {
              i.remove();
            }
          });
        }
      },
      add: (source) => {
        if (this.histogram) {
          source.records.forEach((record) => {
            this.histogram.project.assignmentStore.add(record.toJSON());
          });
        }
      },
      update: async (source) => {
        if (this.histogram) {
          const { id, ...data } = source.record.toJSON();
          const record = this.histogram.project.assignmentStore.getById(id);
          if (record) {
            record.set(data);
          }
        }
      },
    });

    let moveBeforeStartToast;
    let afterTaskDropToast;
    this.g.columns.on("move", () => {
      let max = this.g.columns.data.length;
      this.g.columns.forEach((el) => {
        el.data.order = max--;
      });
    });
    this.g.on({
      dependencyValidationComplete: ({ source, target, data }) => {
        if (source.inactive || target.inactive) {
          // block dependencies creation on inactive tasks
          data.valid = false;
          return false;
        }
      },
      beforeTaskEditShow: ({ editor, taskRecord }) => {
        const endDate = editor.widgetMap.endDate;
        const startDate = editor.widgetMap.startDate;
        const duration = editor.widgetMap.duration;
        const effort = editor.widgetMap.effort;
        const constraintType = editor.widgetMap.constraintTypeField;
        const constraintDate = editor.widgetMap.constraintDateField;
        let disabled = taskRecord.blocked;
        const canMoveExplicit = this.generalProjectSettings.AllowToMoveExplicitAssignment !== false;
        const explicit = !canMoveExplicit && taskRecord.ExplicitAssignment;
        if (explicit || taskRecord.inactive) {
          disabled = true;
        }

        constraintType.disabled = disabled;
        constraintDate.disabled = disabled;
        endDate.disabled = disabled;
        effort.disabled = disabled;
        startDate.disabled = disabled;
        duration.disabled = disabled;
      },
      afterTaskDrop: ({ context, source }) => {
        source.refresh();

        if (
          context.record.RidderType === ridderTypes.R_MAINPROJECT &&
          context.startDate.getTime() < new Date(project.startDate).getTime()
        ) {
          if (moveBeforeStartToast && moveBeforeStartToast.hide) {
            moveBeforeStartToast.hide();
          }
          moveBeforeStartToast = Toast.show({
            html: translate("projectMoveBeforeStartDate"),
            timeout: 5000,
            showProgress: true,
          });
        }
      },
      beforeTaskDropFinalize: (event) => {
        const canMoveExplicit = this.generalProjectSettings.AllowToMoveExplicitAssignment !== false;

        if (event.context.valid && event.context.record.ExplicitAssignment && !canMoveExplicit) {
          event.context.valid = false;

          if (afterTaskDropToast && afterTaskDropToast.hide) {
            afterTaskDropToast.hide();
          }

          afterTaskDropToast = Toast.show({
            html: translate("cantMoveExplicitAssignments"),
            timeout: 5000,
            showProgress: true,
          });
        }
      },
      beforeCellEditStart: ({ editorContext }) => {
        const { record, column } = editorContext;
        const dateField = [
          "startDate",
          "endDate",
          "fullDuration",
          "fullEffort",
          "constraintDate",
          "constraintType",
        ].includes(column.field);
        if (dateField && record.blocked) {
          Toast.show(translate("cantChangeBlockedTaskField", { field: column.field }));
          return false;
        }
        if (dateField && record.ExplicitAssignment) {
          const canMoveExplicit = this.generalProjectSettings.AllowToMoveExplicitAssignment !== false;
          if (!canMoveExplicit) {
            Toast.show(translate("cantMoveExplicitAssignments"));
            return false;
          }
        }
        if (dateField && record.inactive) {
          Toast.show(translate("cantChangeInactiveTaskField"));
          return false;
        }
      },
    });
    this.g.features.print.exportDialog.on({
      beforeShow: ({ source }) => {
        this.setState({
          exportInProgress: true,
        });
        const project = this.props.api.gantt.project;
        source.widgetMap.rangeStartField.value = DateHelper.add(project.startDate, -7, 'day');
        source.widgetMap.rangeEndField.value = DateHelper.add(project.endDate, 7, 'day');

        source.widgetMap.paperFormatField.items.sort((a, b) => {
          const valueA = (a.value || '').toLowerCase();
          const valueB = (b.value || '').toLowerCase();
          return valueA.localeCompare(valueB);
        }); 
      },
      hide: () => {
        this.setState({
          exportInProgress: false,
        });
      },
    });
  };

  get readOnly() {
    return this.props.access?.AccessLevel === PAGE_ACCESS.Read;
  }

  setPageTitle = ({ mainProject, pageTitle, project }) => {
    const readOnly = this.readOnly;
    this.pageTitle = `${mainProject?.RidderProjectNumber ?? mainProject?.RidderOrderNumber} - ${pageTitle}`;
    this.pageTitleSufix = (readOnly ? " (Read only)" : "") + (project?.NewProject ? ` (${translate("new")})` : "");

    this.props.dispatch(changePageTitle(this.pageTitle + this.pageTitleSufix));
    document.title = this.pageTitle + " | Project Assistant";
  };

  getTasks = async (project, baselineProject) => {
    this.setState({
      loading: true,
      loadingText: translate("Retrieving project tasks..."),
      loadingExtraText: [],
    });

    const params = {
      project: project,
    };

    if (baselineProject) {
      params.baselineProject = baselineProject;
    }

    const projectDataStream = await this.context.get(apiUrls.projectGantt, params);
    const projectData = await projectDataStream.json();

    this.setPageTitle(projectData);
    this.projectData = projectData;

    const calendarParams = {
      includeServiceAppointments: false,
    };
    const resourcesAndCalendarsStream = await this.context.get(apiUrls.GetRidderResourcesAndCalendars, calendarParams);
    const resourcesAndCalendarsData = await resourcesAndCalendarsStream.json();

    const {
      projectData: prData,
      resourcesData,
      calendarsData,
      tasksData,
    } = await generateDataset(
      projectData,
      !(this.generalProjectSettings.AllowToMoveExplicitAssignment !== false),
      resourcesAndCalendarsData
    );
    this.resourcesData = resourcesData;
    this.calendarsData = calendarsData;
    this.tasksData = tasksData;

    checkResources(this, this.resourcesData);

    prData.listeners = this.getProjectListeners();
    const projectModel = new ProjectModel(prData);
    this.setState(
      {
        loading: false,
        loadingText: null,
        currentProject: projectData.project,
      },
      async () => {
        const gantt = this.g;
        window.gantt = gantt; // setGlobal access for debug
        gantt.project && gantt.project.destroy();
        gantt.project = projectModel;
        await this.addCustomToolbarItems(projectData.project);
        this.applyEvents(projectModel);
        if (this.state.isHistogramVisible) {
          this.activateHistogram();
        }
      }
    );
  };

  histogramAbortController;
  getHistogramData = async () => {
    try {
      if (this.histogramAbortController) {
        this.histogramAbortController();
      }
      const { abort, request } = await this.context.getWithAbort(apiUrls.HistogramDataByDepartment, {
        //departmentID: this.g.selectedRecord?.FK_DEPARTMENT || undefined,
        endDate: this.g.project.endDate.toISOString(),
        startDate: this.g.project.startDate.toISOString(),
        //projectID: this.projectID,
      });
      this.histogramAbortController = abort;
      const resp = await request;
      this.histogramAbortController = null; // clear abort when we got data

      const { tasks, bryntumAssignments } = await resp.json();
      await AsyncHelper.sleep(10);
      return {
        tasks,
        bryntumAssignments,
      };
    } catch (e) {
      console.error(e);
      this.histogramAbortController = null;
      throw e;
    }
  };

  tasks = {};
  busy = false;
  updateHistogram = async () => {
    if (this.histogram?.isVisible) {
      if (this.busy) {
        this.tasks.next = () => {
          this.updateHistogram();
          this.tasks.next = null;
        };
        return;
      }
      this.busy = true;
      Mask.mask(
        {
          text: translate("Updating histogram"),
          mode: "dark",
        },
        this.histogram.element
      );
      try {
        const { tasks, bryntumAssignments } = await this.getHistogramData();
        const openRecords = this.histogram.project.resourceStore
          .map((i) => i)
          .filter((i) => !i.meta.collapsed && i.meta.childCount)
          .map((i) => i.id);
        await this.histogramProject.assignmentStore.loadDataAsync(bryntumAssignments);
        await this.histogramProject.eventStore.loadDataAsync(tasks);
        await this.histogram.collapseAll();
        await this.histogram.expandAll();
        await this.histogram.collapseAll();
        for (const row of openRecords) {
          await this.histogram.features.group.toggleCollapse(row, false);
        }
        Mask.unmask(this.histogram.element);
      } catch (e) {
        console.log(e);
      } finally {
        this.busy = false;
        if (this.tasks.next) {
          this.tasks.next();
        }
      }
    }
  };
  scrollToDepartment = () => {
    this.histogram.collapseAll();
    const dep = this.g.selectedRecord?.FK_DEPARTMENT;
    if (dep) {
      const resource = this.resourcesData.find((i) => i.departmentId === dep && i.resourceType === 2);
      if (resource) {
        const groupRow = this.histogram.store.allRecords.find(
          (r) => r.meta.groupRowFor === resource?.departmentdescription
        );
        this.histogram.features.group.toggleCollapse(groupRow, false);
        this.histogram.selectRow({ record: resource?.id });
      }
    }
  };

  syncData = async (project1, project2) => {
    project2.taskStore.beginBatch();
    // eslint-disable-next-line  array-callback-return
    project1.taskStore.map((i) => {
      const task = project2.taskStore.getById(i.id);
      if (task) {
        task.set({
          startDate: new Date(i.startDate),
          endDate: new Date(i.endDate),
        });
      }
    });
    project2.taskStore.endBatch();
    project2.assignmentStore.beginBatch();
    // eslint-disable-next-line  array-callback-return
    project1.assignmentStore.map((i) => {
      const assignment = project2.assignmentStore.getById(i.id);
      if (assignment) {
        assignment.set(i.toJSON());
      } else {
        project2.assignmentStore.add(i.toJSON());
      }
    });
    project2.assignmentStore.endBatch();
  };

  activateHistogram = async () => {
    Mask.mask(
      {
        text: translate("Loading histogram"),
        mode: "dark",
      },
      document.body
    );
    const { tasks, bryntumAssignments } = await this.getHistogramData();
    if (!this.histogram) {
      await AsyncHelper.sleep(10);
      this.histogramProject = new ProjectModel({
        calendar: "DefaultCompanyCalendar",
        hoursPerDay: 24,
        daysPerWeek: 5,
        daysPerMonth: 20,
        effectiveDirection: {
          kind: "enforced",
        },
        calendarsData: this.calendarsData,
        resourceStore: {
          useRawData: true,
          data: this.resourcesData,
          sorters: [
            { field: 'sortOrder', ascending: true },
            { field: 'name', ascending: true },
          ],
        },       
        eventsData: tasks,
        assignmentsData: bryntumAssignments,
      });
      await AsyncHelper.sleep(10);
      await this.syncData(this.g.project, this.histogramProject);
      await this.showHistogram();
      this.histogram = new ResourceHistogram({
        appendTo: this.histogramRef.current,
        project: this.histogramProject,
        startDate: new Date(this.g.startDate),
        endDate: new Date(this.g.endDate),
        readOnly: this.props?.access?.AccessLevel === PAGE_ACCESS.Read,
        rowHeight: 30,
        barMargin: 3,
        height: "100%",
        minHeight: "20em",
        showBarTip: true,
        showBarText: true,
        presets: getViewPresets(),
        viewPreset: this.configs.viewPreset || defaultViewPreset,
        getBarText(datum) {
          if (datum.effort) {
            const unit = "hour";
            const allocated = DateHelper.as(unit, datum.effort);
            const available = DateHelper.as(unit, datum.maxEffort);
            return `${Math.round((allocated * 100) / available)}% `;
          }
        },
        features: {
          timeAxisHeaderMenu: {
            items: {
              eventsFilter: false,
              dateRange: false,
              currentTimeLine: false,
            },
          },
          group: {
            field: "departmentdescription",
            groupSortFn: departmentSortResources,
          },
          cellMenu: {
            items: {
              removeRow: false,
            },
          },
        },
        columns: [
          {
            width: 200,
            field: "name",
            text: translate("Resources"),
            region: "left",
          },
          {
            width: 200,
            field: "events.length",
            text: translate("Assignments"),
          },
        ],
      });
      this.props.api.setHistogram(this.histogram);
      this.histogram._effortFormatter = histogramTooltipFormat;
      window.histogramP = this.histogramProject;
      window.histogram = this.histogram;
      const gantt = this.g;
      const preserveVisibleStart =
        gantt?.currentOrientation?.visibleDateRange?.startDate != null
          ? new Date(gantt.currentOrientation.visibleDateRange.startDate.getTime())
          : null;
      await this.histogram.addPartner(gantt);
      // Partnering widens the time axis; scrollToDate alone may not recenter infinite scroll when the target date
      // still lies inside that widened span. shiftToDate runs calculateInfiniteScrollingDateRange first.
      if (preserveVisibleStart) {
        if (typeof gantt.shiftToDate === "function") {
          gantt.shiftToDate(preserveVisibleStart, null);
        }
        if (typeof gantt.nextAnimationFrame === "function") {
          await gantt.nextAnimationFrame();
          await gantt.nextAnimationFrame();
        }
        if (typeof gantt.scrollToDate === "function") {
          await gantt.scrollToDate(preserveVisibleStart, { block: "start" });
        }
      }
      this.scrollToDepartment();
    } else {
      await this.histogramProject.assignmentStore.loadDataAsync(bryntumAssignments);
      await this.histogramProject.eventStore.loadDataAsync(tasks);
      await this.showHistogram();
      this.histogram.show();
    }
    await AsyncHelper.sleep(100);
    Mask.unmask(document.body);
  };

  onSelectionChange = ({ selection, mode, action }) => {
    if (mode === "row") {
      // this.tbar.setDepartment((selection && selection[0]?.FK_DEPARTMENT) || null, this.props.t);
      if (action === "select") {
        this.props.dispatch(projectDetails.actions.onRowSelect(JSON.parse(JSON.stringify(selection[0]))));
      }
      if (action === "deselect") {
        if (selection.length) {
          this.props.dispatch(projectDetails.actions.onRowSelect(JSON.parse(JSON.stringify(selection[0]))));
        }
      }
    }
  };

  toggleHistogramVisible = (newValue) => {
    if (newValue) {
      this.activateHistogram();
    } else {
      this.props.api.histogram?.hide();
      this.hideHistogram();
    }
  };

  goToBaselineProject = (baseline = this.baselineProject) => {
    const gantt = this.g;
    if (!gantt) return;
    const query = new URLSearchParams();
    if (baseline) {
      query.set("baseline", baseline);
    }
    const selectedId = gantt.selectedRecord?.id;
    if (selectedId) {
      query.set("targetTask", selectedId.toString());
    }
    const regions = (gantt.regions || []).map((key) => {
      return {
        key,
        width: gantt.subGrids[key].element.offsetWidth,
      };
    });
    const queryString = query.toString() ? "?" + query.toString() : "";
    const startDate = gantt?.currentOrientation?.visibleDateRange?.startDate;
    const columnFilters = JSON.parse(JSON.stringify(gantt?.project?.taskStore?.filters?._values));
    this.props.history.push({
      pathname: routes.replaceRedirect,
      state: {
        regions,
        startDate: startDate ? new Date(startDate) : null,
        zoomLevel: gantt?.zoomLevel,
        target: routes.project.replace(":id", this.projectID.toString()) + queryString,
        columnFilters: columnFilters
      },
    });
  };

  onRefreshProject = async () => {
    this.leavePageAbort = new AbortController();
    await beforeRefreshPopup(this.leavePageAbort);
    this.leavePageAbort = null;
    this.goToBaselineProject();
  };

  render() {
    const { hasChanges, isHistogramVisible, loading, projectOutdated, projectOutdatedMessage, loadingText, exportInProgress } = this.state;
    const { api } = this.props;
    const readOnly = this.readOnly;
    return (
      <>
        {loading && <Loading loadingText={loadingText} />}
        <UsersOnProject
          onProjectChanged={this.onProjectChanged}
          projectId={this.projectID}
          update={this.props.update}
        />
        <div className={styles.ganttRoot}>
          <Snackbar open={projectOutdated} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
            <Alert variant="filled" severity="warning" elevation={6}>
              {projectOutdatedMessage}
              <Button size="small" variant="contained" color="secondary" onClick={this.onRefreshProject}>
                {translate("Refresh")}
              </Button>
            </Alert>
          </Snackbar>
          <ToolBar overview={this} readOnly={readOnly} />
          <Allotment vertical className={styles.componentsContainer} proportionalLayout={false}>
            <Allotment.Pane priority={1} preferredSize="100%" minSize={100}>
              <BryntumGantt
                {...this.ganttConfig}
                ref={api.setGantt}
                onSelectionChange={this.onSelectionChange}
                readOnly={readOnly}
              />
            </Allotment.Pane>
            <Allotment.Pane snap priority={0} preferredSize="50%" visible={isHistogramVisible} minSize={60}>
              <div className={styles.histogramContainer} ref={this.histogramRef}></div>
            </Allotment.Pane>
          </Allotment>
          <SyncProject
            mainProjectId={this.state.currentProject?.FK_MainProject}
            onRefreshProject={() => this.goToBaselineProject()}
          />
        </div>
        {!readOnly && <StopPageRefresh when={!exportInProgress && hasChanges} />}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    locale: state.i18nState.lang,
  };
};
export default connect(mapStateToProps)(withPageApi(withAccess(Project, AccessEntries.SingleProjectView)));
